import SimplePeer from 'simple-peer';
import { io, Socket } from 'socket.io-client';

export interface WebRTCCallbacks {
  onLocalStream?: (stream: MediaStream) => void;
  onRemoteStream?: (stream: MediaStream) => void;
  onConnectionStateChange?: (state: string) => void;
  onError?: (error: Error) => void;
  onMatchFound?: (data: { partnerId: string; roomId: string; initiator: boolean }) => void;
  onCallEnded?: (reason: string) => void;
  onPartnerDisconnected?: () => void;
}

export class WebRTCService {
  private socket: Socket | null = null;
  private peer: SimplePeer.Instance | null = null;
  private localStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;
  private iceServers: RTCIceServer[] = [];
  private iceTransportPolicy: RTCIceTransportPolicy = 'all'; // 'all' or 'relay' for IP protection
  private callbacks: WebRTCCallbacks = {};
  private roomId: string | null = null;
  private partnerId: string | null = null;
  private sessionStartTime: number | null = null;
  private lastConnectionQuality: string | null = null;
  private qualityCheckInterval: NodeJS.Timeout | null = null;

  constructor(private token: string) {}

  /**
   * Initialize WebSocket connection
   */
  connect(): void {
    const socketUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:4011';

    this.socket = io(`${socketUrl}/video`, {
      auth: { token: this.token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    this.setupSocketListeners();
  }

  /**
   * Set up Socket.io event listeners
   */
  private setupSocketListeners(): void {
    if (!this.socket) return;

    this.socket.on('connected', (data) => {
      console.log('Connected to video server:', data);
    });

    this.socket.on('ice-servers', (data: { iceServers: RTCIceServer[]; iceTransportPolicy?: RTCIceTransportPolicy }) => {
      console.log('ICE servers received:', data.iceServers);
      console.log('ICE transport policy:', data.iceTransportPolicy || 'all');
      this.iceServers = data.iceServers;
      this.iceTransportPolicy = data.iceTransportPolicy || 'all';

      if (data.iceTransportPolicy === 'relay') {
        console.log('⚠️ IP Protection enabled: All traffic will be relayed through TURN server');
      }
    });

    this.socket.on('match-found', (data: { partnerId: string; roomId: string; initiator: boolean }) => {
      console.log('Match found:', data);
      this.partnerId = data.partnerId;
      this.roomId = data.roomId;

      if (this.callbacks.onMatchFound) {
        this.callbacks.onMatchFound(data);
      }

      // Create peer connection
      this.createPeer(data.initiator);
    });

    this.socket.on('offer', (data: { offer: any; roomId: string; from: string }) => {
      console.log('Received offer from:', data.from);
      if (this.peer) {
        this.peer.signal(data.offer);
      }
    });

    this.socket.on('answer', (data: { answer: any; roomId: string; from: string }) => {
      console.log('Received answer from:', data.from);
      if (this.peer) {
        this.peer.signal(data.answer);
      }
    });

    this.socket.on('ice-candidate', (data: { candidate: any; roomId: string; from: string }) => {
      if (this.peer) {
        this.peer.signal(data.candidate);
      }
    });

    this.socket.on('call-ended', (data: { reason?: string }) => {
      console.log('Call ended:', data.reason);
      if (this.callbacks.onCallEnded) {
        this.callbacks.onCallEnded(data.reason || 'Call ended');
      }
      this.destroyPeer();
    });

    this.socket.on('partner-disconnected', (data: { reason?: string }) => {
      console.log('Partner disconnected:', data.reason);
      if (this.callbacks.onPartnerDisconnected) {
        this.callbacks.onPartnerDisconnected();
      }
      this.destroyPeer();
    });

    this.socket.on('match-error', (data: { message: string }) => {
      console.error('Match error:', data.message);
      if (this.callbacks.onError) {
        this.callbacks.onError(new Error(data.message));
      }
    });

    this.socket.on('error', (error: any) => {
      console.error('Socket error:', error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error);
      }
    });

    this.socket.on('disconnect', () => {
      console.log('Disconnected from video server');
    });
  }

  /**
   * Request ICE servers from backend
   */
  async requestICEServers(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Socket not connected'));
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('ICE server request timeout'));
      }, 5000);

      this.socket.emit('get-ice-servers');

      this.socket.once('ice-servers', (data: { iceServers: RTCIceServer[]; iceTransportPolicy?: RTCIceTransportPolicy }) => {
        clearTimeout(timeout);
        this.iceServers = data.iceServers;
        this.iceTransportPolicy = data.iceTransportPolicy || 'all';
        console.log('ICE servers configured:', this.iceServers);
        console.log('ICE transport policy:', this.iceTransportPolicy);
        resolve();
      });
    });
  }

  /**
   * Get local media stream (camera/microphone)
   */
  async getLocalStream(
    audioEnabled: boolean = true,
    videoEnabled: boolean = true,
  ): Promise<MediaStream> {
    try {
      const constraints: MediaStreamConstraints = {
        audio: audioEnabled ? { echoCancellation: true, noiseSuppression: true } : false,
        video: videoEnabled
          ? {
              width: { ideal: 1280 },
              height: { ideal: 720 },
              facingMode: 'user',
            }
          : false,
      };

      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);

      if (this.callbacks.onLocalStream) {
        this.callbacks.onLocalStream(this.localStream);
      }

      return this.localStream;
    } catch (error) {
      console.error('Error accessing media devices:', error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error as Error);
      }
      throw error;
    }
  }

  /**
   * Create WebRTC peer connection
   * Respects IP protection setting (force TURN relay)
   */
  createPeer(initiator: boolean): SimplePeer.Instance {
    console.log('Creating peer, initiator:', initiator);
    console.log('IP Protection mode:', this.iceTransportPolicy === 'relay' ? 'ENABLED' : 'DISABLED');

    this.peer = new SimplePeer({
      initiator,
      trickle: true,
      stream: this.localStream || undefined,
      config: {
        iceServers: this.iceServers.length > 0 ? this.iceServers : [
          { urls: 'stun:stun.l.google.com:19302' },
        ],
        iceTransportPolicy: this.iceTransportPolicy, // 'all' for P2P, 'relay' for IP protection
      },
    });

    this.setupPeerListeners();
    return this.peer;
  }

  /**
   * Set up SimplePeer event listeners
   */
  private setupPeerListeners(): void {
    if (!this.peer) return;

    this.peer.on('signal', (data: any) => {
      console.log('Sending signal:', data.type);

      if (!this.socket || !this.roomId) return;

      if (data.type === 'offer') {
        this.socket.emit('offer', { offer: data, roomId: this.roomId });
      } else if (data.type === 'answer') {
        this.socket.emit('answer', { answer: data, roomId: this.roomId });
      } else {
        // ICE candidate
        this.socket.emit('ice-candidate', { candidate: data, roomId: this.roomId });
      }
    });

    this.peer.on('stream', (stream: MediaStream) => {
      console.log('Received remote stream');
      this.remoteStream = stream;

      if (this.callbacks.onRemoteStream) {
        this.callbacks.onRemoteStream(stream);
      }
    });

    this.peer.on('connect', () => {
      console.log('Peer connection established');

      // Start session timer
      this.sessionStartTime = Date.now();

      // Start monitoring connection quality
      this.startQualityMonitoring();

      if (this.callbacks.onConnectionStateChange) {
        this.callbacks.onConnectionStateChange('connected');
      }
    });

    this.peer.on('close', () => {
      console.log('Peer connection closed');
      if (this.callbacks.onConnectionStateChange) {
        this.callbacks.onConnectionStateChange('closed');
      }
    });

    this.peer.on('error', (err: Error) => {
      console.error('Peer error:', err);
      if (this.callbacks.onError) {
        this.callbacks.onError(err);
      }
    });
  }

  /**
   * Start searching for a match
   */
  findMatch(): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    console.log('Searching for match');
    this.socket.emit('find-match');
  }

  /**
   * Cancel match search
   */
  cancelMatch(): void {
    if (this.socket) {
      this.socket.emit('cancel-match');
    }
  }

  /**
   * End current call
   */
  endCall(reason?: string): void {
    if (this.socket && this.roomId) {
      // Calculate session duration
      const duration = this.sessionStartTime
        ? Math.floor((Date.now() - this.sessionStartTime) / 1000)
        : undefined;

      // Send end-call event with duration and quality
      this.socket.emit('end-call', {
        reason,
        duration,
        connectionQuality: this.lastConnectionQuality || undefined,
      });
    }
    this.destroyPeer();
  }

  /**
   * Toggle audio track
   */
  toggleAudio(enabled: boolean): void {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((track) => {
        track.enabled = enabled;
      });
    }
  }

  /**
   * Toggle video track
   */
  toggleVideo(enabled: boolean): void {
    if (this.localStream) {
      this.localStream.getVideoTracks().forEach((track) => {
        track.enabled = enabled;
      });
    }
  }

  /**
   * Destroy peer connection and clean up
   */
  destroyPeer(): void {
    // Stop quality monitoring
    this.stopQualityMonitoring();

    if (this.peer) {
      this.peer.destroy();
      this.peer = null;
    }

    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = null;
    }

    this.remoteStream = null;
    this.roomId = null;
    this.partnerId = null;
    this.sessionStartTime = null;
    this.lastConnectionQuality = null;
  }

  /**
   * Disconnect from server and clean up
   */
  disconnect(): void {
    this.destroyPeer();

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Set callbacks for events
   */
  setCallbacks(callbacks: WebRTCCallbacks): void {
    this.callbacks = callbacks;
  }

  /**
   * Get current local stream
   */
  getLocalStreamRef(): MediaStream | null {
    return this.localStream;
  }

  /**
   * Get current remote stream
   */
  getRemoteStreamRef(): MediaStream | null {
    return this.remoteStream;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get connection stats for quality monitoring
   */
  async getConnectionStats(): Promise<RTCStatsReport | null> {
    if (this.peer && (this.peer as any)._pc) {
      return (this.peer as any)._pc.getStats();
    }
    return null;
  }

  /**
   * Start monitoring connection quality
   */
  private startQualityMonitoring(): void {
    // Check quality every 5 seconds
    this.qualityCheckInterval = setInterval(async () => {
      const stats = await this.getConnectionStats();
      if (stats) {
        this.lastConnectionQuality = this.assessConnectionQuality(stats);
      }
    }, 5000);
  }

  /**
   * Stop monitoring connection quality
   */
  private stopQualityMonitoring(): void {
    if (this.qualityCheckInterval) {
      clearInterval(this.qualityCheckInterval);
      this.qualityCheckInterval = null;
    }
  }

  /**
   * Assess connection quality based on stats
   */
  private assessConnectionQuality(stats: RTCStatsReport): string {
    let quality = 'good';
    let packetsLost = 0;
    let packetsReceived = 0;

    stats.forEach((report) => {
      if (report.type === 'inbound-rtp' && report.kind === 'video') {
        packetsLost = report.packetsLost || 0;
        packetsReceived = report.packetsReceived || 0;
      }
    });

    if (packetsReceived > 0) {
      const lossRate = packetsLost / (packetsLost + packetsReceived);
      if (lossRate > 0.1) {
        quality = 'poor';
      } else if (lossRate > 0.05) {
        quality = 'fair';
      } else if (lossRate < 0.01) {
        quality = 'excellent';
      }
    }

    return quality;
  }

  /**
   * Get the underlying RTCPeerConnection for quality monitoring
   */
  getPeerConnection(): RTCPeerConnection | null {
    if (this.peer && (this.peer as any)._pc) {
      return (this.peer as any)._pc as RTCPeerConnection;
    }
    return null;
  }

  /**
   * Get the socket instance for external use (e.g., prompt sharing)
   */
  getSocket(): Socket | null {
    return this.socket;
  }
}

export default WebRTCService;
